import React, { useMemo, useState } from 'react';
import { Box, Paper, Typography, Stack, FormControl, Select, MenuItem, Button } from '@mui/material';
import Chart from 'react-apexcharts';
import { exportCsvWithDate } from './csvUtil';

function escapeHtml(str) {
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

const GRAPH_HEIGHT = 300;

const LeaderboardChart = ({ adjustedTeamsMap = {}, teams = {}, defaultTopN = 25 }) => {
  const [topN, setTopN] = useState(defaultTopN);
  const [valueType, setValueType] = useState('adjusted');

  const rows = useMemo(() => Object.values(adjustedTeamsMap || {}), [adjustedTeamsMap]);

  const topRows = useMemo(() => {
    return rows
      .slice()
      .sort((a, b) => (valueType === 'adjusted' ? a.adjustedTotal - b.adjustedTotal : a.originalTotal - b.originalTotal))
      .slice(0, topN);
  }, [rows, topN, valueType]);

  const categories = useMemo(() => topRows.map(t => `${t.teamNumber || ''} ${t.teamName || ''}`.trim()), [topRows]);
  const data = useMemo(() => topRows.map(t => (valueType === 'adjusted' ? t.adjustedTotal : t.originalTotal) || 0), [topRows, valueType]);

  const options = useMemo(() => ({
    chart: { id: 'leaderboard-bar', toolbar: { show: true }, animations: { enabled: false } },
    xaxis: { categories, labels: { rotate: -45 }, title: { text: 'Team' } },
    yaxis: { title: { text: 'Total Score' } },
    dataLabels: { enabled: false },
    tooltip: {
      enabled: true,
      shared: false,
      followCursor: false,
      custom: function({ series, seriesIndex, dataPointIndex, w }) {
        const labels = (w.config.xaxis && w.config.xaxis.categories) || [];
        const label = labels[dataPointIndex] || '';
        const val = series[seriesIndex] && series[seriesIndex][dataPointIndex] !== undefined ? series[seriesIndex][dataPointIndex] : 0;
        const y = (typeof val === 'number') ? val.toFixed(2) : val;
        return `<div style="pointer-events:none;padding:8px;font-size:13px;"><strong>${escapeHtml(label)}</strong><div style="margin-top:6px">Value: <strong>${y}</strong></div></div>`;
      }
    },
    plotOptions: { bar: { horizontal: false } },
  }), [categories]);

  const handleExport = () => {
    const headers = ['uid','teamNumber','teamName','category','division','originalTotal','adjustedTotal','overallPlace','place','mode'];
    const rowsForCsv = topRows.map(r => ({
      uid: r.uid,
      teamNumber: r.teamNumber,
      teamName: r.teamName,
      category: r.category,
      division: r.division,
      originalTotal: r.originalTotal,
      adjustedTotal: r.adjustedTotal,
      overallPlace: r.overallPlace,
      place: r.place,
      mode: r.mode
    }));
    exportCsvWithDate('leaderboard', headers, rowsForCsv);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1">Leaderboard (Top Teams)</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small">
            <Select value={topN} onChange={(e) => setTopN(Number(e.target.value))}>
              <MenuItem value={10}>Top 10</MenuItem>
              <MenuItem value={25}>Top 25</MenuItem>
              <MenuItem value={50}>Top 50</MenuItem>
              <MenuItem value={rows.length}>All</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small">
            <Select value={valueType} onChange={(e) => setValueType(e.target.value)}>
              <MenuItem value="adjusted">Adjusted Total</MenuItem>
              <MenuItem value="original">Original Total</MenuItem>
            </Select>
          </FormControl>

          <Button size="small" variant="outlined" onClick={handleExport}>Export CSV</Button>
        </Stack>
      </Stack>

      <Box sx={{ height: GRAPH_HEIGHT, mt: 2 }}>
        <Chart type="bar" series={[{ name: valueType === 'adjusted' ? 'Adjusted' : 'Original', data }]} options={options} height={GRAPH_HEIGHT} />
      </Box>
    </Paper>
  );
};

export default LeaderboardChart;
